<template>
  <div>
    <Modal v-if="display && !emailCaptureModalFlag" @hideModal="toggleModal" :wide=true customwidth="modalWidth-unset" modalbgCustom="modal-bg-blur">

      <div class="flex flex-wrap font-bold">
        <div class="w-1/2" style="font-size: 1.25rem;color: #00953A;">
          Save Yourself Some Green
        </div>
        <div class="w-1/2" style="font-size: 1.25rem;color: #00953A;">
          $10 Off Your First Purchase
        </div>
      </div>
      <p style="font-size: 0.75rem;"> The sooner you get started, the sooner you'll enjoy your beautiful lawn &
        landscape. </p>

      <div class="flex flex-wrap mt-8">

        <div class="w-full sm:w-1/5 mb-5">
          <img :src="`${urlLocation}/services/lawn-care/service-lawn-care-preferred-program-600x600.webp`"
            style="max-height: 250px; text-align: center; border-radius: 15px; margin: 0 auto;" class="mr-5 p-2"
            alt="" />
        </div>

        <div class="w-full sm:w-3/5 px-6">
          <form @submit.prevent="submitForm" class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">

            <div class="mb-2">
              <label for="firstName" class="block text-gray-700">First Name</label>
              <input v-model="form.firstName" type="text" class="w-full p-2 border rounded-md">
              <span v-if="errors.firstName" class="text-red-500 text-xs">{{ errors.firstName }}</span>

            </div>
            <div class="mb-2">
              <label for="lastName" class="block text-gray-700">Last Name</label>
              <input v-model="form.lastName" type="text" class="w-full p-2 border rounded-md">
              <span v-if="errors.lastName" class="text-red-500 text-xs">{{ errors.lastName }}</span>

            </div>
            <div class="mb-2">
              <label for="email" class="block text-gray-700">Email</label>
              <input v-model="form.emailAddress" type="email" class="w-full p-2 border rounded-md"
                placeholder="Email *">
              <span v-if="errors.emailAddress" class="text-red-500 text-xs">{{ errors.emailAddress }}</span>

            </div>
            <div class="mb-2">
              <label for="phone" class="block text-gray-700">Phone Number</label>
              <MaskInput type="text" :mask="PHONE_INPUT_MASK" placeholder="Phone Number *" autocomplete="tel"
                name="phone" v-model="form.phoneNumber" :value="form.phoneNumber" class="w-full" required />

              <span v-if="errors.phoneNumber" class="text-red-500 text-xs">{{ errors.phoneNumber }}</span>
            </div>
          </form>

          <div style="font-size: 0.75rem;" class="mb-4">
            Valid for first-time US customers and new email subscribers only. Offer valid on Lawn Programs
            only.
            Limit one (1) per household. Cannot be combined with other offers. By entering your email
            address,
            you agree to our Privacy Policy & Terms of Use.
          </div>

          <div style="font-size: 0.9rem;">

            <label class="checkbox flex" for="same_as">
              <span class="checkbox__input">
                <input id="same_as" name="checkbox" type="checkbox" v-model="form.allowText" />
              </span>
              <span class="ml-2">
                By checking this box, you agree to receive text messages (e.g. promotions, service
                notifications/reminders) from Spring Green at the number provided and consent to the
                monitoring and storage of the conversation. Consent is not a condition of purchase.
                Msg & data rates may apply. Msg frequency varies. Unsubscribe at any time by replying STOP or
                clicking the unsubscribe link (where available). HELP for assistance.
                See our <NuxtLink to="/policies/privacy-policy/" target="_blank">Privacy Policy</NuxtLink> and our <NuxtLink to="/policies/terms-use/" target="_blank">Terms of Use</NuxtLink>.
              </span>
            </label>
          </div>
        </div>

        <div class="w-full sm:w-1/5">

          <div class="mt-4 flex justify-end ">
            <a href="#" @click.prevent="submitForm" class="button add-to-cart"> Submit </a>
          </div>
        </div>

      </div>
    </Modal>

    <EmailCaptureCoupon v-if="displayCouponModal" />

  </div>

</template>

<script setup lang="ts">
import { useStore } from 'vuex';
import type { Store } from 'vuex/types/index.js';
import type State from '~/domains/store/State';
import { MaskInput } from 'vue-3-mask';
import {
  formatPhoneNumber,
  getCustomerPhoneNumber,
  getPhoneParts,
  PHONE_INPUT_MASK,
} from '~/utils/phone';
import Modal from '~/components/Modal.vue';
import EmailCaptureCoupon from "~/components/EmailCapture/EmailCaptureCoupon.vue";
import type PersistedState from '~/domains/store/PersistedState';

const { $config, $axios } = useNuxtApp();
const store: Store<State> = useStore();

const display = ref(false);
const displayCouponModal = ref(false);
const emailCaptureModalFlag = ref(false);
const urlLocation = ref($config.public.sgCmsImage);
const form = ref({
  firstName: "",
  lastName: "",
  emailAddress: "",
  phoneNumber: "",
  allowText: false
});
const errors = ref({
  firstName: "",
  lastName: "",
  emailAddress: "",
  phoneNumber: "",
});

watch(
  () => store.state.persistedState.customer,
  (newCustomer, oldCustomer) => {
    nextTick(() => showModal());
  },
  { immediate: true }
);

const showModal = () => {
  display.value = false;
  const persistedState = store.state.persistedState as PersistedState;

  if (!persistedState) return;

  const customer = persistedState.customer;

  emailCaptureModalFlag.value = persistedState?.emailCapture;

  const customerPhone = getCustomerPhoneNumber(customer, "Home") ??
    getCustomerPhoneNumber(customer, "Cell");

  if ((!customer.email && !customer.phone_Home) && customer.size) {
    form.value = {
      firstName: customer.firstName ?? "",
      lastName: customer.lastName ?? "",
      emailAddress: customer.email || "",
      phoneNumber: formatPhoneNumber(customerPhone) ?? "",
      allowText: false,
    };
    display.value = true;
  }
};

const toggleModal = () => {
  store.commit("SET_EMAILCAPTUREFLAG", true);
  display.value = !display.value;
};

const submitForm = async () => {
  // Reset error messages
  errors.value = {
    firstName: "",
    lastName: "",
    emailAddress: "",
    phoneNumber: "",
  };

  let isValid = true;

  // Check if any field is empty and set error messages
  if (!form.value.firstName) {
    errors.value.firstName = "First name is required.";
    isValid = false;
  }
  if (!form.value.lastName) {
    errors.value.lastName = "Last name is required.";
    isValid = false;
  }
  if (!form.value.emailAddress) {
    errors.value.emailAddress = "Email is required.";
    isValid = false;
  }
  if (!getPhoneParts(form.value.phoneNumber)) {
    errors.value.phoneNumber = "Phone number is required.";
    isValid = false;
  }

  // If form is valid, proceed with submission
  if (isValid) {
    store.commit("SET_SHOW_LOADER", true);
    display.value = false;

    const formData = form.value;

    const custDetails = Object.keys(formData).flatMap(key => {
      // Check for phone field
      
      if (key === 'phoneNumber' && formData[key]) {
        const phoneParts = getPhoneParts(formData[key]);
        if (phoneParts) {
          const { areaCode, phonePrefix, phoneNumber } = phoneParts;
          
          return [
            {
              path: 'areaCode',
              op: 'replace',
              value: areaCode,
            },
            {
              path: 'phonePrefix',
              op: 'replace',
              value: phonePrefix,
            },
            {
              path: 'phoneNumber',
              op: 'replace',
              value: phoneNumber,
            },
          ];
        }
      }

      return {
        path: key,
        op: 'replace',
        value: formData[key],
      };
    });

    try {
      const addressLookupID = await getAddressLookUp();

      if (addressLookupID) {

        const responseAddressLookup = await $axios
          .patch(
            `${$config.public.sgApiUrl}Addresses/AddressLookup/${addressLookupID}`,
            custDetails,
            {
              headers: {
                "Content-Type": "application/json",
                "Access-Control-Allow-Origin": "*"
              }
            });

        if (responseAddressLookup.status === 200) {
          displayCouponModal.value = true;
        }

      }

    } catch (error) {
      console.error(error);
    }

    store.commit("SET_SHOW_LOADER", false);

  } else {
    console.warn("Form validation failed.");
  }

};

const getAddressLookUp = async () => {
  const persistedState = store.state.persistedState;
  let addressLookup = persistedState?.addressLookup;

  if (addressLookup) {
    return addressLookup;
  }

  const customer = {
    "custNo": persistedState.cust_No,
    "sqldbid": persistedState.sqldbid,
    "zip": persistedState.zip,
    "address": persistedState?.addressInfo?.address1
  }

  const response = await $axios
    .post(
      `${$config.public.sgApiUrl}Addresses/SG/Search?track=true&useFranchizeZipAssignments=true`,
      customer,
      {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*"
        }
      });

  if (response.status === 200) {
    const data = await response.data;
    let addressInfo = data[0];
    let getAddressLookup = addressInfo?.addressLookupId

    if (getAddressLookup) {
      return getAddressLookup;
    }
  }

}
</script>

<style scoped>
input[type="text"],
input[type="tel"],
input[type="email"] {

  padding: 10px;
  border: 1px solid #ccc;
  margin-top: 5px;
  margin-bottom: 0px;
}

.add-to-cart {
  border-radius: 15px;
  padding: 0.75rem;
  font-size: 1rem;
}
</style>
